import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { getConfig } from '@edx/frontend-platform';
import { getAuthenticatedHttpClient, getAuthenticatedUser } from '@edx/frontend-platform/auth';

import { currentLanguage, getLanguages, useMessages } from './i18n';

async function changeLanguage(code) {
  const config = getConfig();
  const user = getAuthenticatedUser();
  if (user) {
    // Saved on the account, so the LMS and the user's other devices follow.
    try {
      await getAuthenticatedHttpClient().patch(
        `${config.LMS_BASE_URL}/api/user/v1/preferences/${user.username}`,
        { 'pref-lang': code },
        { headers: { 'Content-Type': 'application/merge-patch+json' } },
      );
    } catch (e) {
      // Still switch this browser below.
    }
  }
  // Shared across all ThervuMitra hosts; MFEs and the LMS read it.
  const name = config.LANGUAGE_PREFERENCE_COOKIE_NAME || 'openedx-language-preference';
  const domain = config.LANGUAGE_PREFERENCE_COOKIE_DOMAIN ? `; domain=${config.LANGUAGE_PREFERENCE_COOKIE_DOMAIN}` : '';
  document.cookie = `${name}=${code}; path=/; max-age=31536000; SameSite=Lax; Secure${domain}`;
  window.location.reload();
}

const GlobeIcon = () => (
  <svg className="bm-language__icon" viewBox="0 0 24 24" aria-hidden="true">
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18M12 3c2.5 2.6 3.8 5.6 3.8 9s-1.3 6.4-3.8 9c-2.5-2.6-3.8-5.6-3.8-9S9.5 5.6 12 3z" />
  </svg>
);

const CheckIcon = () => (
  <svg className="bm-language__check" viewBox="0 0 16 16" aria-hidden="true"><path d="M3 8.5l3.2 3L13 4.5" /></svg>
);

// Accessible language menu: a button that opens a listbox of languages, each
// shown in its own script with the English name underneath.
const LanguageSelect = ({ id, tone, placement }) => {
  const t = useMessages();
  const languages = getLanguages();
  const lang = currentLanguage();
  const current = useMemo(() => languages.find(l => l.code === lang) || languages[0], [languages, lang]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(Math.max(0, languages.indexOf(current)));
  const [pending, setPending] = useState(null);
  const root = useRef(null);
  const list = useRef(null);
  const typed = useRef({ text: '', at: 0 });

  useEffect(() => {
    if (!open) { return undefined; }
    const onDown = (e) => { if (root.current && !root.current.contains(e.target)) { setOpen(false); } };
    document.addEventListener('mousedown', onDown);
    list.current?.focus();
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    if (open) {
      list.current?.querySelector(`[data-index="${active}"]`)?.scrollIntoView({ block: 'nearest' });
    }
  }, [active, open]);

  const choose = (language) => {
    setOpen(false);
    if (language.code !== current.code) {
      setPending(language.code);
      changeLanguage(language.code);
    }
  };

  const onKeyDown = (e) => {
    const last = languages.length - 1;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(last, a + 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(0, a - 1)); }
    else if (e.key === 'Home') { e.preventDefault(); setActive(0); }
    else if (e.key === 'End') { e.preventDefault(); setActive(last); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); choose(languages[active]); }
    else if (e.key === 'Escape' || e.key === 'Tab') {
      setOpen(false);
      root.current?.querySelector('button')?.focus();
    } else if (e.key.length === 1) {
      // Type-to-find on the English name (e.g. "ta" jumps to Tamil).
      const now = Date.now();
      typed.current = { text: (now - typed.current.at < 700 ? typed.current.text : '') + e.key.toLowerCase(), at: now };
      const hit = languages.findIndex(l => l.name.toLowerCase().startsWith(typed.current.text));
      if (hit >= 0) { setActive(hit); }
    }
  };

  const listId = `${id}-list`;
  return (
    <div ref={root} className={`bm-language bm-language--${tone || 'light'} bm-language--${placement || 'down'}${open ? ' is-open' : ''}`}>
      <button
        type="button"
        id={id}
        className="bm-language__button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={`${t.language}: ${current.label}`}
        onClick={() => { setActive(Math.max(0, languages.indexOf(current))); setOpen(o => !o); }}
      >
        <GlobeIcon />
        <span lang={current.code} dir={current.dir || 'ltr'}>{pending ? '…' : current.label}</span>
        <svg className="bm-language__chevron" viewBox="0 0 12 8" aria-hidden="true"><path d="M1 1.5l5 5 5-5" /></svg>
      </button>
      {open && (
        <div className="bm-language__panel">
          <div className="bm-language__title">{t.chooseLanguage}</div>
          <ul
            ref={list}
            id={listId}
            role="listbox"
            tabIndex={-1}
            aria-label={t.language}
            aria-activedescendant={`${id}-opt-${active}`}
            onKeyDown={onKeyDown}
            className="bm-language__list"
          >
            {languages.map((l, i) => (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events
              <li
                key={l.code}
                id={`${id}-opt-${i}`}
                data-index={i}
                role="option"
                aria-selected={l.code === current.code}
                className={`bm-language__option${i === active ? ' is-active' : ''}`}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(l)}
              >
                <span className="bm-language__native" lang={l.code} dir={l.dir || 'ltr'}>{l.label}</span>
                <span className="bm-language__english">{l.name}</span>
                {l.code === current.code && <CheckIcon />}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default LanguageSelect;
